import React, { useState, useEffect, useCallback } from 'react';
import { Pagination, Input, Form, Select, message } from 'antd';
import moment from 'moment';
import { getAllMeetingsAdmin } from './meetingFeedbackApi';
import FeedbackModal from './FeedbackModal';
import LoaderImage from '../LoaderImage';
import NoDataFound from '../../layouts/NoDataFound';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
const STATUS_CONFIG = {
  active: { label: 'Active', color: '#12B76A', bg: '#ECFDF3' },
  upcoming: { label: 'Upcoming', color: '#1570EF', bg: '#EFF8FF' },
  expired: { label: 'Completed', color: '#F04438', bg: '#FEF3F2' },
};

dayjs.extend(utc);
dayjs.extend(timezone);

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Completed' },
];

const tableHeaders = [ 'Package','Host','Host Ip', 'Planned','Started',  'Duration', 'Invited','Joined', 'Status', 'Feedback'];

/* ── Meeting Feedback (admin) ──────────────────────────────────────────────────
   Two levels, same as the host's own view in the web portal: every event across
   all hosts here, and the attendee feedback for one event behind the Feedback
   button on each row.
─────────────────────────────────────────────────────────────────────────────── */
function FeedbackListing() {
  const [form] = Form.useForm();
  const [events, setEvents] = useState([]);
  const [count, setCount] = useState(0);
  const [loader, setLoader] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  // { id, hostId, title } of the event whose feedback modal is open
  const [feedbackMeeting, setFeedbackMeeting] = useState(null);

  const loadEvents = useCallback(
    async ({ page = 1, size = pageSize, searchTxt = search, statusTxt = status } = {}) => {
      setLoader(true);
      const res = await getAllMeetingsAdmin({
        limit: size,
        offset: size * (page - 1),
        search: searchTxt,
        status: statusTxt,
      });
      setLoader(false);

      if (!res.success) {
        message.error(res.message || 'Failed to load events');
        setEvents([]);
        setCount(0);
        return;
      }
      setEvents(res.data);
      setCount(res.count);
    },
    [pageSize, search, status]
  );

  useEffect(() => {
    loadEvents({ page: 1 });
    // Intentionally on mount only — search and status changes call loadEvents
    // themselves with the new value, before state has settled.
  }, []);

  const onPageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
    loadEvents({ page, size });
  };

  const onFinish = (values) => {
    const searchTxt = values.searchTxt || '';
    setSearch(searchTxt);
    setCurrentPage(1);
    loadEvents({ page: 1, searchTxt });
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const handleOnChange = (e) => {
    // Clearing the box restores the full list without pressing Search.
    if (e.target.value.length === 0) {
      setSearch('');
      setCurrentPage(1);
      loadEvents({ page: 1, searchTxt: '' });
    }
  };

  const onStatusChange = (value) => {
    setStatus(value);
    setCurrentPage(1);
    loadEvents({ page: 1, statusTxt: value });
  };

  return (
    <div className='dashboard-card pb-5 container-fluid'>
      <div className='row p-0 pb-4'>
        <div
          className='for-before col-12 col-md-7 head fs-3 fw-bold'
          style={{ backgroundColor: '#a7c1d9', borderBottomRightRadius: '10px', color: '#FFFFFF' }}
        >
          Meeting Feedback
        </div>
      </div>

      <Form
        form={form}
        name='basic'
        initialValues={{ status: '', searchTxt: '' }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete='off'
        className='row p-3 pb-0'
      >
        <div className='col-md-2 col-6'>
          <Form.Item colon={false} name='status' style={{ marginBottom: 0 }}>
            <Select
              placeholder='Status'
              options={STATUS_OPTIONS}
              onChange={onStatusChange}
              style={{ width: '100%', height: '44px', borderRadius: '8px' }}
            />
          </Form.Item>
        </div>
        <div className='col-md-3 col-6'>
          <Form.Item colon={false} name='searchTxt' style={{ marginBottom: 0 }}>
            <Input
              placeholder='Search title, package or host id'
              onChange={handleOnChange}
              style={{ width: '100%', height: '44px', borderRadius: '8px' }}
            />
          </Form.Item>
        </div>
        <div className='col-md-2 col-12 ml-3' style={{ paddingTop: '0.1rem' }}>
          <Form.Item colon={false} style={{ marginBottom: 0 }}>
            <button
              type='submit'
              className='btn btn-primary button pl-3'
              style={{ height: '44px', borderRadius: '8px', padding: '0 18px' }}
            >
              Search
            </button>
          </Form.Item>
        </div>
      </Form>

      <div className='table-scrollbar'>
        {loader && <LoaderImage />}
        {events && events.length > 0 && !loader ? (
          <table id='customers' className='w-100'>
            <thead>
              <tr>
                {tableHeaders.map((header) => (
                  <th className='px-3' key={header}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className='w-100'>
              {events.map((event) => {
                const statusCfg = STATUS_CONFIG[event.displayStatus] || STATUS_CONFIG.upcoming;
                const eventMoment = moment(`${event.date} ${event.time}`, 'YYYY-MM-DD HH:mm');
                const timeLabel = eventMoment.isValid() ? eventMoment.format('hh:mm A') : event.time;

      // const eventMomentstart = moment(`${event.startedAt}`, 'YYYY-MM-DD HH:mm');
      //           const timeLabelstart = eventMomentstart.isValid() ? eventMomentstart.format('hh:mm A') : event.time;
                return (
                  <tr key={event.id}>
                    <td>{event.package ? event.package : 'null'}</td>
                    <td>{event.hostName ? event.hostName : event.hostId}</td>
                    <td>{event.hostIp ? event.hostIp : '-'}</td>

                    {/* <td>{event.title ? event.title : 'null'}</td> */}
                    <td style={{ width: '220px' }}>
                       {event.date} {timeLabel} {event.timeZone}
                    
                    </td>
                   <td style={{ width: '220px' }}>
  {event.startedAt && event.timeZone
    ? dayjs
        .utc(event.startedAt)
        .tz(event.timeZone)
        .format("YYYY-MM-DD hh:mm A") 
    : "-"}    {event.startedAt && event.timeZone ? event.timeZone:' '}
</td>
                    <td>{event.meetingDurationMinutes}</td>
                    <td>
                      {event.activeContactCount}/{event.maxMembers}
                    </td>
                    <td>{event.countofjoinedParticipants}</td>

                    <td>
                      <span
                        style={{
                          padding: '0.25rem 0.9rem',
                          borderRadius: '50rem',
                        //   backgroundColor: statusCfg.bg,
                        //   color: statusCfg.color,
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {statusCfg.label}
                      </span>
                    </td>
                    <td className='edit-data'>
                      <button
                        type='button'
                        className='btn'
                        style={{ borderRadius: '7px', padding: '2px 12px', whiteSpace: 'nowrap' }}
                        onClick={() => setFeedbackMeeting({ id: event.id, hostId: event.hostId, title: event.title })}
                      >
                        Feedback ({event.feedbackCount})
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <>{!loader && <NoDataFound />}</>
        )}
      </div>

      <div className='row'>
        {currentPage >= 0 && count ? (
          <Pagination
            style={{ marginTop: '10px' }}
            onChange={onPageChange}
            current={currentPage}
            pageSize={pageSize}
            total={count}
            showTotal={(total) => `Total ${total} items`}
            showSizeChanger
          />
        ) : (
          ' '
        )}
      </div>

      <FeedbackModal
        isOpen={!!feedbackMeeting}
        onClose={() => setFeedbackMeeting(null)}
        meetingId={feedbackMeeting?.id}
        hostId={feedbackMeeting?.hostId}
        meetingTitle={feedbackMeeting?.title}
      />
    </div>
  );
}

export default FeedbackListing;
